import { CREDIT_LIMITS, CREDIT_MEANING, CREDIT_READ_AGAIN } from "./credit-words.ts";
import type { Words } from "./site-words.ts";

// The site's words in Spanish (S10.02).

export const ES: Words = {
  tagline: "IA que gestiona tu negocio",
  signIn: "Iniciar sesión",
  signUp: "Registrarse",
  demoLabel: "Pruébalo",
  howItWorksLabel: "Cómo funciona",
  howItWorksTitle: "De una audiencia a una lista corta",
  howItWorksDescription: "Tres pasos, un solo espacio de trabajo. Sin hojas de cálculo de por medio.",
  productLabel: "Producto",
  productTitle: "Y también lo que viene después de la búsqueda",
  productDescription:
    "Encontrar leads es el comienzo. {product} lee cada contacto, completa lo que puede y guarda tu lista corta en un solo lugar, lista para exportar.",
  faqLabel: "Preguntas",
  faqTitle: "Preguntas, respondidas",
  homeTitle: "{product} · Leads de Instagram con contacto público",
  brandTitle: "Marca y prensa · {product}",
  notFoundTitle: "Página no encontrada · {product}",
  homeDescription:
    "Encuentra leads de Instagram con correo o teléfono público, desde los seguidores de un competidor o las cuentas que sigue.",
  homeKeyphrase: "leads de Instagram",
  brandDescription: "Logos, colores y las palabras para escribir sobre {product}, listos para descargar.",
  heroEyebrow: "Prospección con IA en una sola herramienta",
  heroTitleLead: "Leads frescos de Instagram",
  heroTitleTail: "desde",
  heroSources: ["@competidor", "seguidores", "seguidos"],
  platforms: {
    instagram: "Instagram",
    x: "X.com",
    googleMaps: "Google Maps",
    tiktok: "TikTok",
    linkedin: "LinkedIn",
  },
  heroSourcesLabel: "la cuenta de un competidor, sus seguidores o las cuentas que sigue",
  heroLedeLead: "Competidores y otras cuentas que ya siguen tus compradores.",
  heroLedeTail: " lee esa audiencia y te la deja lista.",
  heroStart: "Empieza gratis",
  heroNote: "Sin tarjeta de crédito. Tu primer análisis usa créditos gratis.",
  analyses: "Análisis",
  demoPlaceholder: "@una cuenta que siguen tus compradores",
  demoAction: "Leer la audiencia",
  demoReading: "Leyendo la audiencia…",
  demoHandleLabel: "Usuario de la cuenta",
  demoFollowersRead: "Seguidores leídos",
  demoBusinesses: "Negocios",
  demoWithEmail: "Con correo público",
  demoTableLabel: "Leads en la audiencia de @acme_company",
  demoShowing: "Mostrando 1–{shown} de {total}",
  demoCaption:
    "Una vista previa con negocios inventados y contactos ocultos: el producto real lee perfiles públicos, correos incluidos. Abre cualquier lead.",
  demoPlaces: {
    businesses: [
      {
        kind: "Estudio de diseño de interiores",
        bio: "Hogares tranquilos y llenos de luz. Proyectos residenciales en toda la ciudad.",
      },
      {
        kind: "Tienda de productos orgánicos",
        bio: "Local, de temporada y orgánico. Cajas frescas a domicilio los viernes.",
      },
      {
        kind: "Clínica de fisioterapia",
        bio: "Lesiones deportivas y rehabilitación. Reserva tu primera evaluación en línea.",
      },
      {
        kind: "Estudio de pilates",
        bio: "Clases de reformer para todos los niveles. Grupos pequeños, grandes resultados.",
      },
      { kind: "Floristería", bio: "Flores para bodas y eventos, y flores semanales para oficinas." },
      {
        kind: "Tostador de café de especialidad",
        bio: "Granos de origen único tostados en lotes pequeños. Venta al por mayor.",
      },
      { kind: "Boutique de novias", bio: "Vestidos de novia a medida. Pruebas solo con cita previa." },
      {
        kind: "Tienda de nutrición deportiva",
        bio: "Suplementos y planes para corredores, ciclistas y levantadores.",
      },
      { kind: "Clínica dental", bio: "Odontología familiar y blanqueamiento. Pacientes nuevos este mes." },
      { kind: "Panadería", bio: "Masa madre, pasteles y tortas a pedido, horneados cada mañana." },
      {
        kind: "Estudio de yoga",
        bio: "Vinyasa, yin y yoga prenatal. La primera clase va por nuestra cuenta.",
      },
      { kind: "Barbería", bio: "Cortes clásicos y afeitado con toalla caliente. Sin cita hasta las 18:00." },
    ],
    cities: ["Madrid", "Valencia", "Lisboa", "Ámsterdam", "Milán", "Berlín", "Barcelona", "Oporto"],
  },
  columns: {
    name: "Nombre y usuario",
    email: "Correo",
    phone: "Teléfono",
    followers: "Seguidores",
    website: "Sitio web",
  },
  detail: {
    close: "Cerrar lead",
    fit: "Encaje",
    email: "Correo público",
    phone: "Teléfono",
    website: "Sitio web",
    business: "Negocio",
    followers: "Seguidores",
    following: "Seguidos",
  },
  defaultProject: "Proyecto principal",
  newAnalysis: "Nuevo análisis",
  nav: {
    dashboard: "Panel",
    analyses: "Análisis",
    prospects: "Prospectos",
  },
  journey: [
    {
      eyebrow: "1 · Encuentra",
      title: "Apunta a una audiencia",
      description:
        "Los seguidores de una cuenta, o las cuentas que sigue. {product} lee los perfiles públicos de esa audiencia y deja fuera las cuentas privadas.",
    },
    {
      eyebrow: "2 · Califica",
      title: "Quédate con los que puedes contactar",
      description:
        "Correos y teléfonos públicos, leídos directamente del perfil. Busca y ordena por nicho, tamaño y ciudad para convertir miles en una lista corta.",
    },
    {
      eyebrow: "3 · Usa",
      title: "Lleva tu lista a donde quieras",
      description:
        "Expórtala a una hoja de cálculo o a tu CRM, o enriquece un lead con más de lo que publica. Cada contacto guarda de dónde salió.",
    },
  ],
  features: [
    {
      title: "Todas las plataformas, una búsqueda",
      description: "Seguidores y seguidos de Instagram hoy; X, Google Maps, TikTok y LinkedIn llegan pronto.",
    },
    {
      title: "De dónde vienen tus contactos",
      description:
        "Cada contacto viene del perfil público, o es un correo probable, construido a partir de su sitio web.",
    },
    {
      title: "Enriquece lo que importa",
      description:
        "Activa el enriquecimiento automático y {product} completa el sitio web, la categoría y los enlaces de un lead a medida que los encuentra.",
    },
    {
      title: "Exporta con un clic",
      description:
        "Descarga la lista completa o una selección en CSV, lista para una hoja de cálculo o tu CRM.",
    },
    {
      title: "Funciona desde Claude",
      description:
        "Pídele a Claude que analice una cuenta o resuma tus leads, y ocurre en tu espacio de trabajo.",
    },
  ],
  stats: [
    { value: "1 cuenta", label: "basta para empezar" },
    { value: "< 2 min", label: "de un usuario a una lista corta" },
    { value: "1 crédito", label: "lee 1 perfil público de Instagram" },
  ],
  questions: [
    {
      question: "¿De dónde salen los contactos?",
      answer: `De perfiles públicos de Instagram, de empresas y de personas: el correo, el teléfono y el sitio web que cada uno publica. ${CREDIT_LIMITS.es} Cada contacto guarda de dónde salió.`,
    },
    {
      question: "¿Qué plataformas puedo leer?",
      answer:
        "Instagram hoy: los seguidores de una cuenta y las cuentas que sigue. X, Google Maps, TikTok y LinkedIn llegan pronto. Cuéntanos qué necesitas y lo priorizaremos.",
    },
    {
      question: "¿Cómo se cuentan los créditos?",
      answer: `${CREDIT_MEANING.es} ${CREDIT_READ_AGAIN.es} Tu primer análisis usa créditos gratis, y puedes poner un límite a cualquier análisis.`,
    },
    {
      question: "¿Cuándo puedo usarlo?",
      answer: "Hoy. Crea una cuenta y tu primer análisis usa créditos gratis.",
    },
  ],
  ctaTitle: "Tu lista corta está a un usuario de distancia",
  ctaDescription: "Nombra una cuenta que siguen tus compradores y obtén tus primeros leads en minutos.",
  footer: {
    product: "Producto",
    features: "Funciones",
    company: "Empresa",
    brand: "Marca y prensa",
    contact: "Contacto",
    resources: "Recursos",
  },
  legal: "© 2026 {product}. Todos los derechos reservados.",
  notFound: {
    trail: "No encontrado",
    addressLabel: "La dirección que abriste",
    tableLabel: "Resultados para esta dirección",
    title: "Esta página no existe",
    emptyTitle: "No hay nada en esta dirección",
    emptyDescription: "Prueba la página de inicio, o nombra una cuenta para leer su audiencia.",
    description: "El enlace puede ser antiguo, o la dirección estar mal escrita.",
    home: "Volver al inicio",
  },
  cookies: {
    title: "Usamos cookies",
    body: "Usamos cookies para analizar el tráfico y mejorar tu experiencia. Acepta todas, quédate solo con las necesarias o elige cuáles permitir. Puedes cambiarlo cuando quieras en Opciones de privacidad, al pie de cada página.",
    reject: "Solo necesarias",
    accept: "Aceptar todas",
    choose: "Personalizar",
    choicesTitle: "Opciones de privacidad",
    choicesDescription: "Elige qué puede usar este sitio. Lo estrictamente necesario está siempre activo.",
    save: "Guardar opciones",
    requiredTitle: "Estrictamente necesarias",
    requiredDescription:
      "Recuerda esta elección y mantiene tu sesión si usas {product}. No se usa para rastreo.",
    requiredLabel: "Siempre activas",
    analyticsTitle: "Analítica",
    analyticsDescription:
      "Cuenta las visitas y relaciona una visita con un registro. Desactivada, tus visitas no se miden.",
    advertisingTitle: "Publicidad",
    advertisingDescription:
      "Mide y mejora nuestros anuncios. Desactivada, no se carga ninguna herramienta publicitaria.",
  },
};
